import {
  BackgroundCheckStatus,
  BackgroundCheckStatusLabelMap,
  ClassEligibilityLabelMap,
  classEligibilitySchema,
  CoachAttendanceGrade,
  CoachAttendanceGradeLabelMap,
  CoachOverallGrade,
  CoachOverallGradeColorMap,
  CoachOverallGradeLabelMap,
  CoachSession,
  CoachSessionsResults,
  CoachStatus,
  CoachStatusLabelMap,
  CoachStatusColorMap,
  coachStatusSchema,
  CoachStatusTree,
  CoachSubStatus,
  CoachSubStatusLabelMap,
  GetCoachesFilter,
  TechCheckTypeLabelMap,
  TechCheckType,
  EligibleCoachForClassTime,
  coachOverallGradeRankOrder,
  TeachingPreference,
  TeachingPreferenceLabelMap,
  LoggedInUser,
} from '@airreading/common/data';
import { createFormOptions } from './form-utils';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { Temporal } from 'temporal-polyfill';
import { QueryClient } from '@tanstack/react-query';
import { formatDateWithDayOfWeek, formatTimeRange, FrequentTimeZones } from '@airreading/common';

export const allCoachStatuses = Object.values(CoachStatus);

export function getCoachStatusLabel(status: CoachStatus | null | undefined): string {
  if (!status) return '-';
  return CoachStatusLabelMap[status] ?? '-';
}

export function getCoachStatusColor(status: CoachStatus | null | undefined): string {
  if (!status) return '';
  return CoachStatusColorMap[status] ?? '';
}

export function getBackgroundCheckStatusLabel(status: BackgroundCheckStatus | null | undefined): string | null {
  if (!status) return null;
  return BackgroundCheckStatusLabelMap[status] ?? null;
}

export const backgroundCheckStatusOptions = Object.values(BackgroundCheckStatus).map((value) => ({
  label: BackgroundCheckStatusLabelMap[value],
  value,
}));

export function getTechCheckTypeLabel(tech: TechCheckType | null | undefined): string | null {
  if (!tech) return null;
  return TechCheckTypeLabelMap[tech] ?? null;
}

export const techCheckStatusOptions = Object.values(TechCheckType).map((value) => ({
  label: TechCheckTypeLabelMap[value],
  value,
}));

export const classEligibilityOptions = classEligibilitySchema.options.map((value) => ({
  label: ClassEligibilityLabelMap[value],
  value,
}));

export function getCoachAttendanceGradeLabel(attendanceGrade: CoachAttendanceGrade | null | undefined): string {
  if (!attendanceGrade) return '-';
  return CoachAttendanceGradeLabelMap[attendanceGrade] ?? '-';
}

export function getCoachAttendanceGradeOptions() {
  return Object.values(CoachAttendanceGrade)
    .filter((value) => value !== CoachAttendanceGrade.S)
    .map((value) => ({
      value,
      label: getCoachAttendanceGradeLabel(value),
    }));
}

export function getCoachOverallGradeOptions() {
  return Object.values(CoachOverallGrade).map((value) => ({
    value,
    label: getCoachOverallGradeLabel(value),
  }));
}

export function getCoachOverallGradeLabel(overallGrade: CoachOverallGrade | null | undefined): string {
  if (!overallGrade) return '-';
  return CoachOverallGradeLabelMap[overallGrade] ?? '-';
}

export function getCoachOverallGradeColor(overallGrade: CoachOverallGrade | null | undefined): string {
  if (!overallGrade) return '';
  return CoachOverallGradeColorMap[overallGrade] ?? '';
}

export function getTeachingPreferenceLabel(preference: TeachingPreference | null | undefined): string {
  if (!preference) return '-';
  return TeachingPreferenceLabelMap[preference] ?? '-';
}

export function getTeachingPreferencesLabel(preferences: TeachingPreference[] | null | undefined): string {
  if (!preferences || preferences.length === 0) return '-';
  const hasRegular = preferences.includes(TeachingPreference.regular_schedule);
  const hasSubstitute = preferences.includes(TeachingPreference.substitute_teaching);
  if (hasRegular && hasSubstitute) return 'Both regular and substitute';
  if (hasRegular) return 'Regular schedule only';
  if (hasSubstitute) return 'Substitute teaching only';
  return '-';
}

export const coachStatusOptions = createFormOptions<CoachStatus>(coachStatusSchema.options, CoachStatusLabelMap);

export const coachStatusGroupedOptions = coachStatusSchema.options.map((status) => ({
  label: CoachStatusLabelMap[status],
  value: status,
  children: CoachStatusTree[status].map((subStatus: CoachSubStatus) => ({
    label: CoachSubStatusLabelMap[subStatus],
    value: subStatus,
  })),
}));

export enum AllUpdateTeacherTypeValues {
  Available = 'available',
  All = 'all',
}

export const AllSelectTeacherTypes: MultiSelectOption[] = [
  {
    label: 'Available teachers only',
    value: AllUpdateTeacherTypeValues.Available,
  },
  {
    label: 'All teachers',
    value: AllUpdateTeacherTypeValues.All,
  },
];

export interface CoachesTypeaheadSearchOptions {
  shouldDisplayOverallGrade?: boolean;
  filters?: Omit<GetCoachesFilter, 'query'>;
}

export interface SubstituteCoachConflictsWithSessionProps {
  originalCoachId: string;
  substituteCoachId: string;
  session: {
    id: string;
    startInstant: Temporal.Instant;
    endInstant: Temporal.Instant;
  };
  queryClient: QueryClient;
  start?: Temporal.ZonedDateTime;
  end?: Temporal.ZonedDateTime;
}

export interface SubstituteCoachConflictResult {
  coachHasConflicts: boolean;
  coachSessions: CoachSessionsResults['coachSessions'];
}

export interface DialogSubstituteCoachConflictText {
  title: string;
  description: string;
  confirmationText: string;
}

export function getDialogSubstituteCoachConflictText(coachSession: CoachSession): DialogSubstituteCoachConflictText {
  const { schoolName, startInstant, endInstant, substitutionRequests } = coachSession;
  const lastSubstitutionRequest =
    substitutionRequests.length > 0 ? substitutionRequests[substitutionRequests.length - 1] : null;
  const fullNameOrEmail = lastSubstitutionRequest?.substituteCoach?.fullNameOrEmail || 'The substitute teacher';
  const date = formatDateWithDayOfWeek(startInstant, FrequentTimeZones.UsCentral);
  const time = formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral);

  return {
    title: `${fullNameOrEmail} is scheduled for a conflicting session:`,
    description: `${date} - ${time} - ${schoolName || 'Unknown School'}.`,
    confirmationText: 'Are you sure you want to override and assign them to this session?',
  };
}

export const sortByOverallGrade = (coaches: EligibleCoachForClassTime[]) => {
  return [...coaches].sort((a, b) => {
    const aGrade = a.overallGrade;
    const bGrade = b.overallGrade;

    if (aGrade === null && bGrade === null) return 0;
    if (aGrade === null) return 1;
    if (bGrade === null) return -1;

    const aIndex = coachOverallGradeRankOrder.indexOf(aGrade);
    const bIndex = coachOverallGradeRankOrder.indexOf(bGrade);

    return aIndex - bIndex;
  });
};

export const getOverallGradeIndex = (grade: CoachOverallGrade) => {
  if (!grade) return Number.MAX_SAFE_INTEGER;
  return coachOverallGradeRankOrder.indexOf(grade);
};

export const isCoachOffboarded = (loggedInUser: LoggedInUser | null | undefined) => {
  return loggedInUser?.coachStatus === CoachStatus.offboarded;
};

export const isCoachOnboarding = (loggedInUser: LoggedInUser | null | undefined) => {
  return loggedInUser?.coachStatus === CoachStatus.onboarding;
};
